/*
* Pipeline Theme — Aquiesse custom JavaScript
*
* Keep custom JS here so Pipeline stays easy to update. This file is loaded via
* the (uncommented) custom.js <script> import in layout/theme.liquid.
*/

(function () {
  'use strict';

  // ── Accessibility: backfill missing `alt` on client-side / filter-generated images ──────────────
  // Some images are injected after page load by third-party JS (Judge.me review cards) or emitted
  // without an alt by Shopify's own filters (the <img> fallback inside `video_tag`). A static Liquid
  // alt isn't possible for those, so they trip axe's `image-alt` (WCAG 1.1.1). We watch the specific
  // containers that produce them and set an appropriate alt. Scoped on purpose — we do NOT touch
  // images elsewhere, so genuine missing-alt bugs in theme templates still surface normally.
  var ALTLESS_SELECTOR = [
    '[class*="jdgm"] img:not([alt])',              // Judge.me review / media cards
    'video img:not([alt])',                        // video_tag fallback poster
    '[data-recently-viewed-products] img:not([alt])',
    '[data-predictive-search-results] img:not([alt])'
  ].join(', ');

  function altFor(img) {
    // Judge.me review photo — prefer the card's product/author label, else a generic descriptor.
    if (img.closest('[class*="jdgm"]')) {
      var card = img.closest('.jdgm-card, .jdgm-review, .jdgm-media-card, .jdgm-rev');
      var label = card && card.querySelector(
        '.jdgm-card__product, .jdgm-rev__author, .jdgm-review__author, .jdgm-card__author'
      );
      var text = label && label.textContent ? label.textContent.trim() : '';
      return text ? text.slice(0, 125) : 'Customer review photo';
    }
    // Video poster fallback and other supplementary thumbnails are decorative in context
    // (adjacent text/title conveys the meaning) — an empty alt is the correct, axe-passing choice.
    return '';
  }

  function backfill(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.matches && root.matches(ALTLESS_SELECTOR) && !root.hasAttribute('alt')) {
      root.setAttribute('alt', altFor(root));
    }
    if (root.querySelectorAll) {
      var imgs = root.querySelectorAll(ALTLESS_SELECTOR);
      for (var i = 0; i < imgs.length; i++) {
        if (!imgs[i].hasAttribute('alt')) imgs[i].setAttribute('alt', altFor(imgs[i]));
      }
    }
  }

  // ── Free-shipping progress bar ───────────────────────────────────────────────────────────────
  // The bar fill is painted server-side by snippets/cart-shipping-message.liquid for a correct first
  // paint; this keeps its width in sync as the cart changes. The existing theme.js CartShippingMessage
  // already handles the `is-success` state toggle and the "$X left to spend" text — we only own width.
  function updateShippingBars(cart) {
    var total = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
    var rate = (window.Shopify && Shopify.currency && Shopify.currency.rate)
      ? parseFloat(Shopify.currency.rate) : 1;
    if (!rate || isNaN(rate)) rate = 1;
    var fills = document.querySelectorAll('[data-free-shipping-bar-fill]');
    for (var i = 0; i < fills.length; i++) {
      var limit = parseFloat(fills[i].getAttribute('data-limit')) || 0;
      if (limit <= 0) continue;
      var limitCents = limit * 100 * rate;
      var pct = Math.max(0, Math.min(100, (total / limitCents) * 100));
      fills[i].style.width = pct + '%';
      var bar = fills[i].closest('[role="progressbar"]');
      if (bar) bar.setAttribute('aria-valuenow', Math.round(pct));
    }
  }

  // ── Cart upsell carousel ─────────────────────────────────────────────────────────────────────
  // Populates each [data-cart-upsell] with cart-upsell-card tiles, anchored on the highest-value
  // cart line item. Source cascade:
  //   1. Search & Discovery "complementary products" (recommendations API, intent=complementary).
  //   2. Shopify related products (intent=related).
  //   3. Curated / whole-catalog fallback rendered server-side into <template data-cart-upsell-fallback>.
  // Products already in the cart are always excluded. Variant products open an inline options
  // sheet (image + pills + price + Add) instead of a slide-out drawer.
  var UPSELL_SECTION = 'api-cart-upsell';

  function idsToSet(str) {
    var set = {};
    (str || '').split(',').forEach(function (part) {
      var v = part.trim();
      if (v) set[v] = true;
    });
    return set;
  }

  function cardProductId(node) {
    return node.getAttribute('data-product-id') || '';
  }

  // Add a variant to the cart, then refresh the cart drawer/page + shipping bar + upsell.
  function cartAdd(variantId) {
    var routes = (window.theme && window.theme.routes) || {};
    var addUrl = (routes.cart_add_url || '/cart/add') + '.js';
    var cartUrl = (routes.cart || '/cart') + '.js';
    return window.fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return window.fetch(cartUrl, { credentials: 'same-origin' }).then(function (c) { return c.json(); });
    }).then(function (cart) {
      document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart } }));
      document.dispatchEvent(new CustomEvent('theme:cart:reload'));
      return cart;
    });
  }

  // ── Shared variant options sheet (one <dialog> for the whole page) ──
  var sheetDialog = null;
  function ensureSheet() {
    if (sheetDialog) return sheetDialog;
    var d = document.createElement('dialog');
    d.className = 'cu-sheet';
    d.setAttribute('data-cu-sheet-dialog', '');
    d.innerHTML =
      '<div class="cu-sheet__panel">' +
        '<button type="button" class="cu-sheet__close" data-cu-sheet-close aria-label="Close">&times;</button>' +
        '<div class="cu-sheet__body" data-cu-sheet-body></div>' +
      '</div>';
    document.body.appendChild(d);

    // backdrop click closes (clicks on the dialog element itself = the backdrop area)
    d.addEventListener('click', function (e) {
      if (e.target === d) closeSheet();
    });
    d.addEventListener('cancel', function () { /* native Esc → close, allow default */ });
    d.querySelector('[data-cu-sheet-close]').addEventListener('click', closeSheet);

    // delegated pill selection + confirm add
    d.addEventListener('click', function (e) {
      var pill = e.target.closest('[data-cu-pill]');
      if (pill) { selectPill(d, pill); return; }
      var confirm = e.target.closest('[data-cu-add-confirm]');
      if (confirm) {
        if (confirm.getAttribute('aria-disabled') === 'true') return;
        var id = confirm.getAttribute('data-variant-id');
        if (!id) return;
        confirm.setAttribute('aria-disabled', 'true');
        cartAdd(id).then(closeSheet).catch(function () {
          confirm.setAttribute('aria-disabled', 'false');
        });
      }
    });
    sheetDialog = d;
    return d;
  }

  function selectPill(dialog, pill) {
    if (pill.getAttribute('aria-disabled') === 'true') return;
    dialog.querySelectorAll('[data-cu-pill]').forEach(function (p) { p.setAttribute('aria-checked', 'false'); });
    pill.setAttribute('aria-checked', 'true');
    var priceEl = dialog.querySelector('[data-cu-sheet-price]');
    var price = pill.getAttribute('data-variant-price');
    if (priceEl && price) priceEl.textContent = price;
    var confirm = dialog.querySelector('[data-cu-add-confirm]');
    if (confirm) {
      confirm.setAttribute('data-variant-id', pill.getAttribute('data-variant-id'));
      confirm.setAttribute('aria-disabled', 'false');
    }
  }

  function openSheet(card) {
    var tpl = card.querySelector('template[data-cu-sheet]');
    if (!tpl || !tpl.content) return;
    var d = ensureSheet();
    var body = d.querySelector('[data-cu-sheet-body]');
    body.innerHTML = '';
    body.appendChild(tpl.content.cloneNode(true));
    if (typeof d.showModal === 'function') d.showModal();
    else d.setAttribute('open', '');
  }

  function closeSheet() {
    if (!sheetDialog) return;
    if (typeof sheetDialog.close === 'function' && sheetDialog.open) sheetDialog.close();
    else sheetDialog.removeAttribute('open');
  }

  function CartUpsell(root) {
    this.root = root;
    this.track = root.querySelector('[data-cart-upsell-track]');
    var tpl = root.querySelector('[data-cart-upsell-fallback]');
    this.fallbackCards = tpl && tpl.content
      ? Array.prototype.slice.call(tpl.content.querySelectorAll('.cu-card'))
      : [];
    this.max = parseInt(root.getAttribute('data-max'), 10) || 4;
    this.recommendUrl = root.getAttribute('data-recommend-url') || '/recommendations/products/';
    this.reqId = 0;
    if (!this.track) return;

    // carousel arrows
    this.nav = root.querySelector('[data-cart-upsell-nav]');
    this.prevBtn = root.querySelector('[data-cart-upsell-prev]');
    this.nextBtn = root.querySelector('[data-cart-upsell-next]');
    var self = this;
    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.scrollByPage(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.scrollByPage(1); });
    this.track.addEventListener('scroll', function () { self.updateNav(); }, { passive: true });
    window.addEventListener('resize', function () { self.updateNav(); });

    // card interactions (delegated; survives re-renders)
    this.track.addEventListener('click', function (e) {
      var addBtn = e.target.closest('[data-cu-add]');
      if (addBtn) {
        e.preventDefault();
        var id = addBtn.getAttribute('data-variant-id');
        if (id) { addBtn.classList.add('is-loading'); cartAdd(id).catch(function () { addBtn.classList.remove('is-loading'); }); }
        return;
      }
      var optBtn = e.target.closest('[data-cu-options]');
      if (optBtn) { e.preventDefault(); openSheet(optBtn.closest('[data-cu-card]')); }
    });

    // Seed from data attributes for the first paint, then let the API refine it.
    this.refresh(root.getAttribute('data-anchor'), idsToSet(root.getAttribute('data-cart-ids')));

    document.addEventListener('theme:cart:change', function (event) {
      var cart = event.detail && event.detail.cart;
      if (!cart) return;
      var ids = {};
      var anchor = '';
      var top = -1;
      (cart.items || []).forEach(function (item) {
        ids[String(item.product_id)] = true;
        var price = typeof item.final_price === 'number' ? item.final_price : item.price;
        if (price > top) { top = price; anchor = String(item.product_id); }
      });
      self.refresh(anchor, ids);
    });
  }

  // Fetch one recommendation intent ('complementary' | 'related') as parsed, deduped cards.
  CartUpsell.prototype.fetchIntent = function (anchor, cartIds, intent, limit) {
    var self = this;
    var url = this.recommendUrl + '?section_id=' + UPSELL_SECTION +
      '&limit=' + limit + '&intent=' + intent + '&product_id=' + encodeURIComponent(anchor);
    return window.fetch(url, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.text() : ''; })
      .then(function (html) { return self.parseCards(html, cartIds); });
  };

  // Source cascade for the anchor product:
  //   1. Search & Discovery "complementary products" (intent=complementary)
  //   2. Shopify related products (intent=related)
  //   3. Curated / whole-catalog fallback
  CartUpsell.prototype.refresh = function (anchor, cartIds) {
    var self = this;
    var token = ++this.reqId;

    if (!anchor || !this.recommendUrl) {
      this.render(this.filterFallback(cartIds));
      return;
    }

    var limit = this.max + Object.keys(cartIds).length + 4;
    this.fetchIntent(anchor, cartIds, 'complementary', limit)
      .then(function (complementary) {
        if (token !== self.reqId) return null;
        if (complementary.length) { self.render(complementary); return null; }
        // No complementary set for this product → fall back to related.
        return self.fetchIntent(anchor, cartIds, 'related', limit).then(function (related) {
          if (token !== self.reqId) return null;
          self.render(related.length ? related : self.filterFallback(cartIds));
        });
      })
      .catch(function () {
        if (token !== self.reqId) return;
        self.render(self.filterFallback(cartIds));
      });
  };

  CartUpsell.prototype.parseCards = function (html, cartIds) {
    if (!html) return [];
    var holder = document.createElement('div');
    holder.innerHTML = html;
    var nodes = Array.prototype.slice.call(holder.querySelectorAll('.cu-card'));
    return this.dedupe(nodes, cartIds);
  };

  CartUpsell.prototype.filterFallback = function (cartIds) {
    var clones = this.fallbackCards.map(function (n) { return n.cloneNode(true); });
    return this.dedupe(clones, cartIds);
  };

  // Drop cards already in the cart and duplicate product ids, then cap at max.
  CartUpsell.prototype.dedupe = function (nodes, cartIds) {
    var seen = {};
    var out = [];
    for (var i = 0; i < nodes.length && out.length < this.max; i++) {
      var id = cardProductId(nodes[i]);
      if (!id || cartIds[id] || seen[id]) continue;
      seen[id] = true;
      out.push(nodes[i]);
    }
    return out;
  };

  CartUpsell.prototype.render = function (cards) {
    if (!this.track) return;
    this.track.innerHTML = '';
    cards.forEach(function (node) { this.track.appendChild(node); }, this);
    this.root.classList.toggle('is-populated', cards.length > 0);
    backfill(this.track);
    this.track.scrollLeft = 0;
    var self = this;
    if (window.requestAnimationFrame) window.requestAnimationFrame(function () { self.updateNav(); });
    else this.updateNav();
  };

  CartUpsell.prototype.updateNav = function () {
    if (!this.nav) return;
    var t = this.track;
    var overflow = t.scrollWidth - t.clientWidth > 4;
    this.nav.hidden = !overflow;
    if (this.prevBtn) this.prevBtn.disabled = t.scrollLeft <= 2;
    if (this.nextBtn) this.nextBtn.disabled = t.scrollLeft >= (t.scrollWidth - t.clientWidth - 2);
  };

  CartUpsell.prototype.scrollByPage = function (dir) {
    var card = this.track.querySelector('.cu-card');
    var step = card ? (card.getBoundingClientRect().width + 12) * 2 : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  function initCartUpsell(root) {
    var els = root.querySelectorAll ? root.querySelectorAll('[data-cart-upsell]') : [];
    for (var i = 0; i < els.length; i++) {
      if (!els[i].__cartUpsell) els[i].__cartUpsell = new CartUpsell(els[i]);
    }
  }

  function init() {
    backfill(document.body);
    document.addEventListener('theme:cart:change', function (event) {
      if (event.detail && event.detail.cart) updateShippingBars(event.detail.cart);
    });
    initCartUpsell(document);
    // Judge.me carousels clone/inject nodes continuously; watch for added subtrees and backfill them.
    // Also pick up any cart upsell container that gets rendered after load.
    var observer = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          backfill(added[n]);
          if (added[n].nodeType === 1) initCartUpsell(added[n]);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
